import apiClient from '../../../common/services/apiClient';
import { contractStatus } from '../../../common/enums';
import { removeFalsyProperties } from '../../../common/utils/helpers';

export const apiCallIds = {
  SIGN_UP: 'SIGN_UP',
  LOGIN: 'LOGIN',
  TOKEN_LOGIN: 'TOKEN_LOGIN',
  SIGN_CONTRACT: 'SIGN_CONTRACT',
  SIGN_CONTRACT_WITH_INSURANCE: 'SIGN_CONTRACT_WITH_INSURANCE',
  CONFIRM_CONTRACT: 'CONFIRM_CONTRACT',
  SIGN_INFO_OBLIGATIONS_INVESTOR: 'SIGN_INFO_OBLIGATIONS_INVESTOR',
  CONFIRM_INFO_OBLIGATIONS_INVESTOR: 'CONFIRM_INFO_OBLIGATIONS_INVESTOR',
  FETCH_ACCOUNT_CONTRACTS: 'FETCH_ACCOUNT_CONTRACTS',
  UPDATE_ACCOUNT_PROFILE: 'UPDATE_ACCOUNT_PROFILE',
  CHANGE_PASSWORD: 'CHANGE_PASSWORD',
  FETCH_INVESTED_AMOUNT: 'FETCH_INVESTED_AMOUNT',
  CHANGE_NOTIFICATIONS: 'CHANGE_NOTIFICATIONS',
  FETCH_LOAN_CREATION_ATTEMPT: 'FETCH_LOAN_CREATION_ATTEMPT',
  UPDATE_LOAN_CREATION_ATTEMPT: 'UPDATE_LOAN_CREATION_ATTEMPT',
  TOGGLE_AUTOINVEST: 'TOGGLE_AUTOINVEST',
};

export function getInsuranceType(form) {
  let insuranceType = null
  let insuranceTypes = ['A', 'B', 'C']

  for (let key of insuranceTypes) {
    if (form.getFieldValue(`insurance${key}`)) {
      insuranceType = key
    }
  }

  return insuranceType
}

export function getInsuranceInfo(form, insurance) {
  return ({
    packageType: insurance,
    youngerThanLimit: form.getFieldValue('youngerThanLimit'),
    pension: form.getFieldValue('pension'),
    invalidity: form.getFieldValue('invalidity'),
    unableToWork: form.getFieldValue('unableToWork'),
    notes: form.getFieldValue('insuranceNotes'),
    ageRestriction: insurance === 'C' ? 70 : 62,
    inRelationshipWithInsCompany: form.getFieldValue('inRelationshipWithInsCompany'),
    isPoliticalPerson: form.getFieldValue('isPoliticalPerson'),
    isSanctioned: form.getFieldValue('isSanctioned'),
    isFinalBenefitor: form.getFieldValue('isFinalBenefitor'),
    finalBenefitor: form.getFieldValue('finalBenefitor'),
  })
}

export function createBodyForLoanCreationAttempt(step, form, params) {
  let payload;
  switch (step) {
    case 0: {
      payload = {
        "amount": form.amount,
        "months": form.months,
        "goal": form.goal,
        "provisionFee": form.provisionFee,
        "interest": form.interest,
        "payment": form.payment,
        "step": 0,
        "nextPage": form.nextPage ? form.nextPage : "no",
      }
      break;
    }
    case 1: {
      payload = {
        firstName: form.getFieldValue('nameFirst'),
        lastName: form.getFieldValue('nameLast'),
        birthLastName: form.getFieldValue('birthLastName'),
        profileData: {
          "iban": form.getFieldValue('iban'),
          "email": form.getFieldValue('email'),
          "phone": form.getFieldValue('phone'),
          "terms": form.getFieldValue('terms'),
          "gender": form.getFieldValue('gender'),
          "personalNumber": form.getFieldValue('personalNumber'),
        },
        "step": 1,
        "nextPage": form.nextPage ? form.nextPage : "no",
      }
      break;
    }
    case 2: {
      payload = {
        profileData: {
          address: {
            street: form.getFieldValue('address.street'),
            city: form.getFieldValue('address.city'),
            zip: form.getFieldValue('address.zip'),
            yearsAtAddress: form.getFieldValue('yearsAtAddress'),
          },
          "idCardNumber": form.getFieldValue('idCardNumber'),
          "maritalStatus": form.getFieldValue('maritalStatus'),
          "kids": form.getFieldValue('kids'),
        },
        "step": 2,
        "nextPage": form.nextPage ? form.nextPage : "no",
      }
      break;
    }
    case 3: {
      payload = {
        profileData: {
          "education": form.getFieldValue('education'),
          "socialStatus": form.getFieldValue('socialStatus'),
          "employer": form.getFieldValue('employer'),
          "incomeFrom": form.getFieldValue('incomeFrom'),
          "sector": form.getFieldValue('sector'),
          "declarationOfCorrectInformation": form.getFieldValue('declarationOfCorrectInformation'),
          "obligations": params && params.obligations ? params.obligations : [],
          "income": {
            "monthlyAvg": form.getFieldValue('income.monthlyAvg'),
          },
          "costs": {
            "monthlyAvg": form.getFieldValue('costs.monthlyAvg'),
          },
          "insuranceTaxDebt": form.getFieldValue('insuranceTaxDebt'),
        },
        "obligations": params && params.obligations ? params.obligations : [],
        "step": 3,
        "nextPage": form.nextPage ? form.nextPage : "no",
      }
      break;
    }
  }

  const insurance = getInsuranceType(form)
  let info = null

  if (insurance) {
    info = getInsuranceInfo(form, insurance)
    info.interestDiscount = params.interestDiscount
    info.insuranceAmount = params.insuranceAmount
  }

  payload.insurance = insurance
  payload.insuranceInfo = info

  if (params) {
    payload.payment = params.payment;
    payload.interest = params.interest;
    payload.insuranceAmount = insurance ? params.insuranceAmount : null;
  }

  form.nextPage = "no";
  return payload;
}

export default {
  signUp: (type, accountData) => {
    delete accountData.repeatPassword;

    return apiClient.post(`/account/${type}`, accountData, {
      apiCallId: apiCallIds.SIGN_UP,
    });
  },

  login: ({ email, password, captchaToken }) => {
    return apiClient.post('/auth/login', { email, password, captchaToken }, { apiCallId: apiCallIds.LOGIN });
  },

  forgottenPassword: ({ email }) => {
    return apiClient.post('/auth/forgotten-password', { email });
  },

  resetPassword: ({ email, password, token }) => {
    return apiClient.post('/auth/reset-password', {
      email,
      password,
      token,
    });
  },

  activateAccount: ({ accountId, activationToken }) => {
    return apiClient.get(`/account/${accountId}/activate/${activationToken}`);
  },

  fetchMe: accountId => {
    return apiClient.get(`/account/${accountId}`);
  },

  fetchMeByToken: () => {
    return apiClient.get('/auth/me');
  },

  fetchTrustSnapToken: accountId => {
    return apiClient.get(`/account/${accountId}/trustSnapToken`);
  },

  fetchUnsignedContracts: accountId => {
    return apiClient.get(`/account/${accountId}/contract?status=${contractStatus.INACTIVE}`);
  },

  fetchPendingContracts: accountId => {
    return apiClient.get(`/account/${accountId}/contract?status=${contractStatus.PENDING_CONFIRMATION}`);
  },

  fetchAccountContracts: accountId => {
    return apiClient.get(`/account/${accountId}/contract`, {
      apiCallId: apiCallIds.FETCH_ACCOUNT_CONTRACTS,
    });
  },

  signContract: (accountId, contractId) => {
    return apiClient.post(`/account/${accountId}/contract/${contractId}/sign`, undefined, {
      apiCallId: apiCallIds.SIGN_CONTRACT,
    });
  },

  signContracts: (accountId, contracts) => {
    return apiClient.post(`/account/${accountId}/signContracts`, {contracts}, {
      apiCallId: apiCallIds.SIGN_CONTRACT,
    });
  },

  confirmContract: (accountId, token, confirmBy) => {
    return apiClient.post(
      `/account/${accountId}/contract/${confirmBy === 'contractNumber' ? 'confirmNumber' : 'confirmSmsCode'
      }`,
      { token },
      { apiCallId: apiCallIds.CONFIRM_CONTRACT }
    );
  },

  signInfoObligationsInvestor: accountId => {
    return apiClient.post(`/account/${accountId}/signInfoObligationsInvestor`, undefined, {
      apiCallId: apiCallIds.SIGN_INFO_OBLIGATIONS_INVESTOR,
    });
  },

  confirmInfoObligationsInvestor: (accountId, token) => {
    return apiClient.post(
      `/account/${accountId}/confirmInfoObligationsInvestor`,
      { token },
      { apiCallId: apiCallIds.CONFIRM_INFO_OBLIGATIONS_INVESTOR }
    );
  },

  resendInfoObligationsInvestorSmsCode: accountId => {
    return apiClient.get(`/account/${accountId}/resendInfoObligationsInvestorSmsCode`);
  },

  resendSmsCodeContract: accountId => {
    return apiClient.get(`/account/${accountId}/contract/resendSmsCode`);
  },

  resendActivationEmail: accountId => {
    return apiClient.get(`/account/${accountId}/resendActivationEmail`);
  },

  updateAccountProfile: (accountId, profileData) => {
    return apiClient.put(`/account/${accountId}/profile`, removeFalsyProperties(profileData), {
      apiCallId: apiCallIds.UPDATE_ACCOUNT_PROFILE,
    });
  },

  changePassword: (accountId, passwordNew, passwordOld) => {
    return apiClient.put(
      `/account/${accountId}/password`,
      { passwordNew, passwordOld },
      {
        apiCallId: apiCallIds.CHANGE_PASSWORD,
      }
    );
  },

  checkIfExists: ({ email, personalNumber, phone, iban }) => {
    return apiClient.get(
      `/account/exists?email=${email}&personalNumber=${personalNumber}&phone=${phone}&iban=${iban}`,
      {
        apiCallId: false,
      }
    );
  },

  fetchInvestedAmount: accountId => {
    return apiClient.get(`/account/${accountId}/investedAmount`, {
      apiCallId: apiCallIds.FETCH_INVESTED_AMOUNT
    });
  },

  updateLoanCreationAttempt: (cookieId, body) => {
    return apiClient.put(`loan/attempt/${cookieId}`, body, {
      apiCallId: apiCallIds.UPDATE_LOAN_CREATION_ATTEMPT
    });
  },

  fetchLoanCreationAttempt: cookieId => {
    return apiClient.get(`loan/attempt/${cookieId}`, {
      apiCallId: apiCallIds.FETCH_LOAN_CREATION_ATTEMPT
    });
  },

  changeAccountNotifications: ({ accountId, notifications }) => {
    return apiClient.put(`/account/${accountId}/notifications`,
      { notifications },
      {
        apiCallId: apiCallIds.CHANGE_NOTIFICATIONS,
      });
  },

  toggleAutoInvest: (accountId, data ) => {
    return apiClient.post(`/account/${accountId}/autoInvest`, data, {
      apiCallId: apiCallIds.TOGGLE_AUTOINVEST
    })
  },

  signAutoInvest: (accountId) => {
    return apiClient.put(`/account/${accountId}/autoInvest/sign`, {
      apiCallId: apiCallIds.SIGN_AUTOINVEST
    })
  },

};



// WEBPACK FOOTER //
// ./src/features/auth/api/index.js